import { ByteBuffer } from 'flatbuffers';
import * as turf from '@turf/turf';
import type { MultiPolygon, Position } from 'geojson';

/** Walks a boundary tree and collects each coordinate-leaf as its own ring. */
export function collectRings(
    dv: DataView,
    bytes: Uint8Array,
    nodePos: number,
    depth: number,
    rings: [number, number][][],
): void {
    if (depth > 10 || nodePos < 0 || nodePos + 8 > bytes.length) return;

    const vtAddr = nodePos - dv.getInt32(nodePos, true);
    const fieldOff = vtableField(dv, vtAddr, 0);
    if (!fieldOff) return;

    const fieldAddr = nodePos + fieldOff;
    const vecLenAddr = fieldAddr + dv.getInt32(fieldAddr, true);
    if (vecLenAddr < 0 || vecLenAddr + 4 > bytes.length) return;

    const vecLen = dv.getInt32(vecLenAddr, true);
    if (vecLen <= 0 || vecLen > 200_000) return;

    const vecData = vecLenAddr + 4;
    if (vecData + 4 > bytes.length) return;

    const firstRel = dv.getInt32(vecData, true);
    const firstTarget = vecData + firstRel;
    if (firstTarget < 0 || firstTarget + 12 > bytes.length) return;

    if (readBoundaryCoordPair(dv, firstTarget)) {
        const ring: [number, number][] = [];
        for (let i = 0; i < vecLen; i++) {
            const ea = vecData + i * 4;
            const ca = ea + dv.getInt32(ea, true);
            const pair = readBoundaryCoordPair(dv, ca);
            if (pair) {
                ring.push(pair);
            }
        }
        if (ring.length >= 3) {
            rings.push(ring);
        }
    } else {
        for (let i = 0; i < vecLen; i++) {
            const ea = vecData + i * 4;
            const childPos = ea + dv.getInt32(ea, true);
            collectRings(dv, bytes, childPos, depth + 1, rings);
        }
    }
}

/** Groups rings into MultiPolygon coordinates with hole detection. */
export function ringsToMultiPolygon(rings: [number, number][][]): Position[][][] {
    const closedRings = rings
        .filter((ring) => ring.length >= 3)
        .map((ring) => closeRing(ring));

    if (closedRings.length === 0) return [];

    const sortedRings = closedRings
        .map((ring) => ({
            ring,
            area: Math.abs(turf.area(turf.polygon([ring]))),
        }))
        .sort((a, b) => b.area - a.area);

    type PolygonGroup = { outer: Position[]; holes: Position[][] };
    const groups: PolygonGroup[] = [];

    for (const { ring } of sortedRings) {
        const testPoint = turf.centroid(turf.polygon([ring]));
        let assigned = false;

        for (const group of groups) {
            if (turf.booleanPointInPolygon(testPoint, turf.polygon([group.outer]))) {
                group.holes.push(ring);
                assigned = true;
                break;
            }
        }

        if (!assigned) {
            groups.push({ outer: ring, holes: [] });
        }
    }

    const coordinates = groups.map((group) => [group.outer, ...group.holes]);
    const geometry = turf.rewind(
        { type: 'MultiPolygon', coordinates },
        { reverse: false },
    ) as MultiPolygon;

    return geometry.coordinates;
}

export function closeRing(ring: [number, number][]): Position[] {
    const closed: Position[] = ring.map(([lng, lat]) => [lng, lat] as Position);
    const first = closed[0];
    const last = closed[closed.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
        closed.push([first[0], first[1]]);
    }
    return closed;
}

export function vtableField(dv: DataView, vtAddr: number, fieldIndex: number): number {
    const vtSize = dv.getUint16(vtAddr, true);
    const off = 4 + fieldIndex * 2;
    if (off + 2 > vtSize) return 0;
    return dv.getUint16(vtAddr + off, true);
}

export function isPlausibleCoord(v: number): boolean {
    return Number.isFinite(v) && Math.abs(v) > 1 && Math.abs(v) < 180;
}

/**
 * Admin boundary FlatBuffer coord tables store **latitude at +4** and **longitude at +8**.
 * GeoJSON expects `[longitude, latitude]`.
 */
export function readBoundaryCoordPair(dv: DataView, coordTableAddr: number): [number, number] | null {
    const lat = dv.getFloat32(coordTableAddr + 4, true);
    const lng = dv.getFloat32(coordTableAddr + 8, true);
    if (!isPlausibleCoord(lng) || !isPlausibleCoord(lat)) {
        return null;
    }
    return [lng, lat];
}

export function readFlatBufferString(bb: ByteBuffer, tablePos: number, vtableOff: number): string | null {
    const off = bb.__offset(tablePos, vtableOff);
    return off ? (bb.__string(tablePos + off) as string) : null;
}

export function readFlatBufferInt32(bb: ByteBuffer, tablePos: number, vtableOff: number): number | null {
    const off = bb.__offset(tablePos, vtableOff);
    return off != null && off !== 0 ? bb.readInt32(tablePos + off) : null;
}

export function readFlatBufferFloat32(bb: ByteBuffer, tablePos: number, vtableOff: number): number | null {
    const off = bb.__offset(tablePos, vtableOff);
    return off != null && off !== 0 ? bb.readFloat32(tablePos + off) : null;
}

/** Single outer ring -> Polygon coordinates; multiple parts -> MultiPolygon. */
export function ringsToFieldGeometry(rings: [number, number][][]): MultiPolygon['coordinates'] | null {
    const multi = ringsToMultiPolygon(rings);
    return multi.length > 0 ? multi : null;
}
